import type { Flag, QueueItem } from "@/app/reviewQueries";
import { count } from "@/lib/plural";

/** M1: корзины, сортировки и поиск очереди — на клиенте из строк API
 *  (спека SP4 §2.6). Корзины пересекаются: это фильтры, не разбиение. */

export type BucketId = "all" | "late" | "failing" | "manual";
export type SortId = "wait" | "checks" | "name";
export type Tone = "err" | "warn" | "ok" | "mut" | "info" | "ink";

export const isLate = (r: QueueItem) => r.late;
export const isManual = (r: QueueItem) => r.total === 0;
export const isFailing = (r: QueueItem) => r.total > 0 && r.passed < r.total;

export const BUCKETS: readonly { id: BucketId; label: string; tone: Tone; test: (r: QueueItem) => boolean }[] = [
  { id: "all", label: "вся очередь", tone: "ink", test: () => true },
  { id: "late", label: "просрочено", tone: "err", test: isLate },
  { id: "failing", label: "падают проверки", tone: "warn", test: isFailing },
  { id: "manual", label: "без автопроверок", tone: "info", test: isManual },
];

export const SORTS: readonly { id: SortId; label: string }[] = [
  { id: "wait", label: "по ожиданию" },
  { id: "checks", label: "по проверкам" },
  { id: "name", label: "по имени" },
];

export function bucketCounts(all: QueueItem[]): Record<BucketId, number> {
  return Object.fromEntries(BUCKETS.map((b) => [b.id, all.filter(b.test).length])) as Record<BucketId, number>;
}

export function applyBucket(all: QueueItem[], bucket: BucketId): QueueItem[] {
  const b = BUCKETS.find((x) => x.id === bucket) ?? BUCKETS[0]!;
  return all.filter(b.test);
}

export const cohortLabel = (r: { cohort: string }) => r.cohort || "без когорты";

/** Поиск по имени, файлу, когорте или «без когорты» (трек не ищется). */
export function matches(r: QueueItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return `${r.student.name} ${r.assignment} ${cohortLabel(r)}`.toLowerCase().includes(q);
}

export function filterRows(all: QueueItem[], bucket: BucketId, query: string): QueueItem[] {
  return applyBucket(all, bucket).filter((r) => matches(r, query));
}

/** Доля прошедших проверок; у ручных сдач 2 — они в конце списка. */
const ratio = (r: QueueItem) => (r.total ? r.passed / r.total : 2);

export function sortRows(rows: QueueItem[], sort: SortId): QueueItem[] {
  const out = [...rows];
  switch (sort) {
    case "wait":
      return out.sort((a, b) => b.wait_hours - a.wait_hours);
    case "checks":
      return out.sort((a, b) => ratio(a) - ratio(b) || b.wait_hours - a.wait_hours);
    case "name":
      return out.sort((a, b) => a.student.name.localeCompare(b.student.name, "ru"));
  }
}

export const checksLabel = (r: QueueItem) => (isManual(r) ? "—" : `${r.passed}/${r.total}`);
export const checksTone = (r: QueueItem): Tone => (isManual(r) ? "mut" : isFailing(r) ? "err" : "ok");
export const iterLabel = (r: { iteration: number }) => `v${r.iteration}`;
/** Цвет ожидания: просрочено — красный, больше половины SLA — жёлтый. */
export const waitTone = (r: QueueItem): Tone => (r.late ? "err" : r.wait_hours >= 12 ? "warn" : "mut");
export const rowTone = (r: QueueItem): Tone => (r.late ? "err" : isFailing(r) ? "warn" : "ok");

export const flagLabel = (f: Flag) => `${count(f.count, "возврат", "возврата", "возвратов")} подряд по одной причине — предложить созвон`;

export function countLabel(all: QueueItem[]): string {
  const late = all.filter(isLate).length;
  const noCohort = all.filter((r) => !r.cohort).length;
  return `${count(all.length, "сдача", "сдачи", "сдач")} в работе · ${late} за пределами SLA · ${noCohort} вне когорт`;
}
